import axios from 'axios';
import {
  defineMessage,
  defineMessages,
  MessageDescriptor,
} from '@formatjs/intl';
import type TokenizedApi from '../TokenizedApi';
import type Service from '../modules/Service';
import type { TSchemaValueType } from './schema';
import type { TPermissionRestrictionMessage } from '../modules/TokenizedApiModelEntities';

export interface TErrorInfo {
  fromError?: unknown;
  fromEndpointError?: {
    name: string;
    message: string;
    status?: number | undefined;
    data?: unknown;
    traceId?: string;
  };
  matchesWithFieldName?: string;
  isSafeToIgnoreForBugTracking?: boolean;
}

export class TokenizedError extends Error implements TErrorInfo {
  /** @internal */
  tokenizedApi?: TokenizedApi;
  /** @internal */
  formattedMessage?: MessageDescriptor;
  formattedMessageValues?: Record<string, string>;

  fromError?: Error;

  matchesWithFieldName?: string;

  isSafeToIgnoreForBugTracking = false;

  /** @internal */
  doNotIgnoreIfResultsOptional = false;

  /** @internal */
  constructor(message: string, additionalInfo?: TErrorInfo) {
    super(message);
    if (additionalInfo?.fromError instanceof Error) {
      this.fromError = additionalInfo?.fromError;
    }
    if (additionalInfo?.matchesWithFieldName) {
      this.matchesWithFieldName = additionalInfo.matchesWithFieldName;
    }
    if (additionalInfo?.isSafeToIgnoreForBugTracking) {
      this.isSafeToIgnoreForBugTracking = true;
    }
  }

  /** @internal */
  withFormatting(service: Service, formattedMessage?: MessageDescriptor) {
    this.tokenizedApi = service.tokenizedApi;
    if (formattedMessage) {
      this.formattedMessage = formattedMessage;
    }
    return this;
  }

  get format(): string {
    if (this.tokenizedApi && this.formattedMessage) {
      return this.tokenizedApi.intl
        .getIntl()
        .formatMessage(this.formattedMessage, this.formattedMessageValues);
    }
    return this?.message || `${this}`;
  }
}

export class TokenizedApiError extends TokenizedError {
  fromEndpointError?: {
    name: string;
    message: string;
    status?: number | undefined;
    data?: unknown;
    traceId?: string;
  };

  /** @internal */
  enableRetries = false;

  /** @internal */
  constructor(message: string, additionalInfo?: TErrorInfo) {
    super(message, additionalInfo);
    this.fromEndpointError = additionalInfo?.fromEndpointError;
  }
}

// A non-specific error was detected and reported by the system
export class TokenizedApplicationError extends TokenizedApiError {
  /** @internal */
  constructor(message?: string, additionalInfo?: TErrorInfo) {
    super(message || 'The operation failed', additionalInfo);
    if (!message) {
      this.formattedMessage = defineMessage({
        defaultMessage: 'The operation failed',
        description:
          'Message when the Tokenized back end reports a non-specific error',
        id: 'TokenizedSDK:error.application',
      });
    }
  }
}

// The system itself failed in an unexpected way
export class TokenizedSystemError extends TokenizedApiError {
  /** @internal */
  constructor(message?: string, additionalInfo?: TErrorInfo) {
    super(message || 'The operation failed', additionalInfo);
    if (!message) {
      this.formattedMessage = defineMessage({
        defaultMessage: 'The operation failed',
        description: 'Message when the Tokenized back end fails',
        id: 'TokenizedSDK:error.system',
      });
    }
    this.name = 'System error';
    this.enableRetries = true;
  }
}

// A REST request failed to send, or no valid response was returned
export class TokenizedNetworkError extends TokenizedApiError {
  /** @internal */
  constructor(additionalInfo?: TErrorInfo) {
    super('Unable to communicate with the system', additionalInfo);
    this.name = 'Network error';
    this.formattedMessage = defineMessage({
      defaultMessage: 'Unable to communicate with the system',
      description: 'Error message when a network request fails',
      id: 'TokenizedSDK:error.network',
    });
    this.enableRetries = true;
  }
}

// A specific field in a REST request is invalid
export class TokenizedValidationError extends TokenizedApiError {
  fieldErrors?: Record<string, string>;

  /** @internal */
  constructor(
    message?: string,
    additionalInfo?: TErrorInfo & {
      fieldErrors: Record<string, string>;
    },
  ) {
    super(message || 'The information supplied was invalid', additionalInfo);
    if (!message) {
      this.formattedMessage = defineMessage({
        defaultMessage: 'The information supplied was invalid',
        description: 'Message when a field validation error is reported',
        id: 'TokenizedSDK:error.validation',
      });
    }
    this.name = 'Field validation error';
    this.fieldErrors = additionalInfo?.fieldErrors;
  }
}

// The API credentials are invalid
export class TokenizedAuthError extends TokenizedApiError {
  /** @internal */
  constructor(message?: string | null, additionalInfo?: TErrorInfo) {
    super(message || 'The login session is invalid or has expired', {
      isSafeToIgnoreForBugTracking: true,
      ...additionalInfo,
    });
    if (!message) {
      this.formattedMessage = defineMessage({
        defaultMessage: 'The login session is invalid or has expired',
        description: 'Error message when auth fails',
        id: 'TokenizedSDK:error.auth',
      });
    }
    this.name = 'Authentication error';
    this.doNotIgnoreIfResultsOptional = true;
  }
}

const _getTypeOf = (value: unknown) => typeof value;
type TypeOfNames = ReturnType<typeof _getTypeOf>;

export interface TResponseInvalidInfo extends TErrorInfo {
  type?: TSchemaValueType;
  entityType?: string;
  referenceType?: string;
  path?: string;
  valueType?: TypeOfNames;
}

/**
 * Thrown when an invalid response is received from the Tokenized API.
 * This is most likely to happen when you use an older version of the SDK that
 * doesn’t fully support the current API version.
 */
export class TokenizedResponseInvalidError extends TokenizedApiError {
  type?: TSchemaValueType;
  entityType?: string;
  referenceType?: string;
  path?: string;
  valueType?: TypeOfNames;

  /** @internal */
  constructor(message: string, additionalInfo?: TResponseInvalidInfo) {
    super(message || 'Invalid response', additionalInfo);
    this.name = 'API response error';
    this.type = additionalInfo?.type;
    this.entityType = additionalInfo?.entityType;
    this.referenceType = additionalInfo?.referenceType;
    this.path = additionalInfo?.path;
    this.valueType = additionalInfo?.valueType;
  }
}

export interface TInsufficientFundsInfo extends TErrorInfo {
  instrumentId?: string;
  availableQuantity?: number;
  requestedQuantity?: number;
}

export class TokenizedInsufficientFundsError extends TokenizedApiError {
  instrumentId?: string;
  availableQuantity?: number;
  requestedQuantity?: number;

  /** @internal */
  constructor(additionalInfo?: TInsufficientFundsInfo) {
    super('Insufficient funds to complete transfer', additionalInfo);
    this.formattedMessage = defineMessage({
      defaultMessage: 'Insufficient funds to complete transfer',
      description: 'Error message when attempting a transfer',
      id: 'TokenizedSDK:error.funds.insufficient',
    });
    this.instrumentId = additionalInfo?.instrumentId;
    this.availableQuantity = additionalInfo?.availableQuantity;
    this.requestedQuantity = additionalInfo?.requestedQuantity;
  }
}

export class TokenizedNonTransferableInstrumentError extends TokenizedApiError {
  /** @internal */
  constructor(additionalInfo?: TErrorInfo) {
    super(
      'This transaction includes a non-transferable instrument. Only the issuer is a valid recipient.',
      additionalInfo,
    );
    this.formattedMessage = defineMessage({
      defaultMessage:
        'This transaction includes a non-transferable instrument. Only the issuer is a valid recipient.',
      description: 'Error message when attempting a transfer',
      id: 'TokenizedSDK:error.funds.nontransferable',
    });
  }
}

export type TAttestationFailureReason =
  | 'missing'
  | 'failed'
  | 'incorrect'
  | 'changed'
  | 'reuse';

const FORMAT_ATTESTATION_ERROR: Record<
  TAttestationFailureReason,
  MessageDescriptor
> = defineMessages({
  missing: {
    defaultMessage:
      'Unable to verify that this device meets security requirements (no attestation sent)',
    id: 'TokenizedSDK:error.attestation.missing',
  },
  failed: {
    defaultMessage:
      'Unable to verify that this device meets security requirements (invalid attestation)',
    id: 'TokenizedSDK:error.attestation.failed',
  },
  incorrect: {
    defaultMessage:
      'Unable to verify that this device meets security requirements (incorrect attestation)',
    id: 'TokenizedSDK:error.attestation.incorrect',
  },
  changed: {
    defaultMessage:
      'Unable to verify that this device meets security requirements (inconsistent attestation)',
    id: 'TokenizedSDK:error.attestation.changed',
  },
  reuse: {
    defaultMessage:
      'Unable to verify that this device meets security requirements (attestation reused)',
    id: 'TokenizedSDK:error.attestation.reuse',
  },
});

export class TokenizedAttestationError extends TokenizedApiError {
  attestationFailureReason?: TAttestationFailureReason;

  /** @internal */
  constructor(additionalInfo?: TErrorInfo, reason?: TAttestationFailureReason) {
    super(
      `Unable to verify that this device meets security requirements (reason: ${
        reason || 'unknown'
      })`,
      additionalInfo,
    );
    if (reason && FORMAT_ATTESTATION_ERROR[reason]) {
      this.formattedMessage = FORMAT_ATTESTATION_ERROR[reason];
    } else {
      this.formattedMessage = defineMessage({
        defaultMessage:
          'Unable to verify that this device meets security requirements',
        id: 'TokenizedSDK:error.attestation',
      });
    }
    this.attestationFailureReason = reason;
  }
}

export class TokenizedPermissionRestrictionError extends TokenizedApiError {
  permissionRestrictionMessage?: TPermissionRestrictionMessage;

  /** @internal */
  constructor(
    additionalInfo?: TErrorInfo & {
      permissionRestrictionMessage?: TPermissionRestrictionMessage;
    },
  ) {
    let message =
      additionalInfo?.permissionRestrictionMessage?.message ||
      additionalInfo?.permissionRestrictionMessage?.title;
    if (
      additionalInfo?.permissionRestrictionMessage?.message &&
      additionalInfo?.permissionRestrictionMessage?.title
    ) {
      message = `${additionalInfo?.permissionRestrictionMessage?.title}: ${additionalInfo?.permissionRestrictionMessage?.message}`;
    }
    super(
      message || 'The operation is not currently permitted',
      additionalInfo,
    );
    if (!message) {
      this.formattedMessage = defineMessage({
        defaultMessage: 'The operation is not currently permitted',
        description:
          'Message when an operation has been remotely disabled without any message text',
        id: 'TokenizedSDK:error.permissionRestriction',
      });
    }
    this.name = 'Restricted operation error';
    this.permissionRestrictionMessage =
      additionalInfo?.permissionRestrictionMessage;
  }
}

export interface TServiceErrorInfo extends TErrorInfo {
  serviceName?: string;
  reportedError?: string;
}

export class TokenizedServiceError extends TokenizedApiError {
  serviceName?: string;
  reportedError?: string;

  /** @internal */
  constructor(additionalInfo?: TServiceErrorInfo) {
    const serviceName =
      additionalInfo?.serviceName &&
      typeof additionalInfo?.serviceName === 'string'
        ? additionalInfo.serviceName
        : 'unknown';
    const reportedError =
      additionalInfo?.reportedError &&
      typeof additionalInfo?.reportedError === 'string'
        ? additionalInfo.reportedError
        : 'unknown';

    super(
      `The service “${serviceName}” reported error “${reportedError}”`,
      additionalInfo,
    );

    this.formattedMessage = defineMessage({
      defaultMessage: 'The service “{service}” reported error “{error}”',
      description: 'Error message from a service we depend on',
      id: 'TokenizedSDK:error.service',
    });
    this.formattedMessageValues = {
      service: serviceName,
      error: reportedError,
    };

    this.serviceName = serviceName;
    this.reportedError = reportedError;
  }
}

export class TokenizedExternalServiceError extends TokenizedApiError {
  serviceName?: string;
  reportedError?: string;

  /** @internal */
  constructor(additionalInfo?: TServiceErrorInfo) {
    const serviceName =
      additionalInfo?.serviceName &&
      typeof additionalInfo?.serviceName === 'string'
        ? additionalInfo.serviceName
        : 'unknown';
    const reportedError =
      additionalInfo?.reportedError &&
      typeof additionalInfo?.reportedError === 'string'
        ? additionalInfo.reportedError
        : 'unknown';
    const trimmedReportedError =
      reportedError.length > 250
        ? `${reportedError.slice(0, 250)}…`
        : reportedError;

    super(
      `The third-party service “${serviceName}” failed with error “${trimmedReportedError}”`,
      additionalInfo,
    );

    this.formattedMessage = defineMessage({
      defaultMessage:
        'The third-party service “{service}” failed with error “{error}”',
      description:
        'Error message from third party service (e.g. Paymail host failure)',
      id: 'TokenizedSDK:error.service.external',
    });
    this.formattedMessageValues = {
      service: serviceName,
      error: trimmedReportedError,
    };

    this.serviceName = serviceName;
    this.reportedError = reportedError;
  }
}

export interface TSerializedError {
  formattedErrorMessage: string;
  errorClassName: string;
  errorName: string;
  matchesWithFieldName?: string;
}

export function serializeError(error: Error): TSerializedError | null {
  if (!(error instanceof Error)) {
    return null;
  }

  if (error instanceof TokenizedError) {
    return {
      formattedErrorMessage: error.format,
      errorClassName: error.constructor.name,
      errorName: error.name,
      matchesWithFieldName: error.matchesWithFieldName,
    };
  }

  return {
    formattedErrorMessage: error.message || `${error}`,
    errorClassName: error.constructor.name,
    errorName: error.name,
  };
}

export async function allowToFail<Result>(
  getOptionalResults?: () => Promise<Result>,
  errorDescription?: string,
): Promise<Result | undefined> {
  try {
    return await getOptionalResults?.();
  } catch (error) {
    if (
      error instanceof TokenizedApiError &&
      error.doNotIgnoreIfResultsOptional
    ) {
      throw error;
    }
    console.error(
      errorDescription
        ? `Allowed failure (${errorDescription})`
        : 'Allowed failure',
      error,
    );
  }
}

export function reformatEndpointError(
  endpointError: unknown,
  service: Service,
) {
  if (!(endpointError instanceof Error)) {
    return new TokenizedNetworkError().withFormatting(service);
  }

  if (!axios.isAxiosError(endpointError)) {
    return new TokenizedNetworkError({
      fromEndpointError: {
        name: endpointError.name,
        message: endpointError.message,
      },
    }).withFormatting(service);
  }

  const additionalInfo: TErrorInfo = {
    fromEndpointError: {
      // Note: need to be selective here, since the full
      // endpointError from Axios potentially includes
      // highly sensitive information
      name: endpointError.name,
      message: endpointError.message,
      status: endpointError.response?.status,
      data: endpointError.response?.data,
      traceId: endpointError.config?.headers?.['x-trace'] as string | undefined,
    },
  };

  // Network error
  if (!endpointError.response) {
    return new TokenizedNetworkError(additionalInfo).withFormatting(service);
  }

  // Format an error message using information in the response
  let message;
  const code = endpointError.response.data?.code;
  if (code) {
    // For some error conditions, the API provides structured data,
    // which can be properly formatted for display
    switch (code) {
      case 'INSUFFICIENT_FUNDS':
        return new TokenizedInsufficientFundsError({
          ...additionalInfo,
          instrumentId: endpointError.response.data.instrument_id,
          availableQuantity: endpointError.response.data.available_quantity,
          requestedQuantity: endpointError.response.data.requested_quantity,
        }).withFormatting(service);
      case 'ADMINISTRATOR_INSTRUMENT':
        return new TokenizedNonTransferableInstrumentError(
          additionalInfo,
        ).withFormatting(service);
      case 'SERVICE_FAILURE':
        return new TokenizedServiceError({
          ...additionalInfo,
          serviceName: endpointError.response.data.service,
          reportedError: endpointError.response.data.description,
        }).withFormatting(service);
      case 'EXTERNAL_SERVICE_FAILURE':
        return new TokenizedExternalServiceError({
          ...additionalInfo,
          serviceName: endpointError.response.data.service,
          reportedError: endpointError.response.data.description,
        }).withFormatting(service);
      case 'ATTESTATION_ERROR':
        return new TokenizedAttestationError(
          additionalInfo,
          endpointError.response.data?.message,
        ).withFormatting(service);
      default:
        message = `The operation failed (${code})`;
        break;
    }
  }
  if (endpointError.response.data?.errors?.[0]?.detail) {
    // For errors that don’t have specific handling, the API
    // returns a Go “formatted error”, where each level of the
    // stack gets to add its own string onto the error. These are
    // not very readable, and quite verbose, but do have
    // the information needed to figure out what went wrong
    message = endpointError.response.data.errors[0].detail;
  }

  switch (endpointError.response.status) {
    // Auth error
    // Note that in the mobile app, if a TokenizedAuthError is thrown
    // while connecting a pairing, the app will list that account as
    // requiring re-pairing. Any other TokenizedApiError will cause the
    // account to be in a grayed-out (temporary error) state.
    case 401:
      return new TokenizedAuthError(null, additionalInfo).withFormatting(
        service,
      );

    case 403:
      return new TokenizedPermissionRestrictionError({
        ...additionalInfo,
        permissionRestrictionMessage: endpointError.response.data?.message,
      }).withFormatting(service);

    // Field validation error
    case 422: {
      const fieldErrors: Record<string, string> =
        endpointError.response.data?.errors?.[0]?.meta?.fields?.reduce?.(
          (
            prevErrors: Record<string, string>,
            field: Record<string, unknown> | undefined,
          ) => {
            if (
              field?.field &&
              typeof field.field === 'string' &&
              field?.error &&
              typeof field.error === 'string'
            ) {
              return {
                ...prevErrors,
                [field.field]: field.error,
              };
            }
            return prevErrors;
          },
          {},
        ) || {};
      if (Object.keys(fieldErrors).length) {
        message = Object.entries(fieldErrors)
          .map(([field, error]) => {
            switch (error) {
              case 'required':
                return `“${field}” not specified`;
              case 'incorrect':
                return `“${field}” is incorrect`;
              case 'eqfield':
                return `“${field}” does not match`;
              case 'min':
                return `“${field}” too short/small`;
              case 'email':
                return `“${field}” not a valid email`;
              case 'unconfirmed':
                return `“${field}” not verified`;
              default:
                return `“${field}” ${error}`;
            }
          })
          .join(', ');
      }
      return new TokenizedValidationError(message, {
        ...additionalInfo,
        fieldErrors,
      }).withFormatting(service);
    }

    default:
      if (
        endpointError.response.status >= 400 &&
        endpointError.response.status < 500
      ) {
        return new TokenizedApplicationError(
          message,
          additionalInfo,
        ).withFormatting(service);
      }
      // Consider anything else a system error, even if not in 5xx range
      return new TokenizedSystemError(message, additionalInfo).withFormatting(
        service,
      );
  }
}
